import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { fetchCampaign, patchCampaignPageDesign } from '@/services/campaign';
import { CampaignNotFound } from '@/components/not-found/campaign-not-found';
import { Loading } from '@/components/loader/loading';
import i18next from '@/i18next';
import { errorHandler } from '@/lib/utils';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { CampaignCustomizePanel } from '@/features/page-designs';
import { usePatchBackgroundPattern, usePatchRandomBackgroundPattern, } from '@/features/page-designs/hooks/use-page-design-patterns';
/**
 * Dedicated theme + background-pattern customizer for a campaign. The
 * heavy lifting (picker tabs, iframe preview, layout) lives in the shared
 * `<CampaignCustomizePanel>` so the create flow can reuse it. This route
 * just owns the API patches + optimistic local state.
 *
 * `router.invalidate()` is deliberately not called on the patches — that
 * would trigger the route's `pendingComponent` (`<Loading />`) and unmount
 * the page mid-edit. Theme changes are presentation-only — the toast is
 * enough confirmation, and remount (e.g. tab switch + back) reads server
 * truth from the loader.
 */
export const Route = createFileRoute('/console/_authenticated/_layout/campaigns/$publicId/_layout/customize/')({
    component: CustomizeCampaignPage,
    loader: ({ params: { publicId } }) => fetchCampaign(publicId),
    errorComponent: () => <CampaignNotFound />,
    pendingComponent: () => <Loading loadingText={i18next.t('campaigns.loading.campaign', { ns: 'glossary' })}/>,
});
function CustomizeCampaignPage() {
    const campaign = Route.useLoaderData();
    const { workspace } = useAuthenticationState();
    const proUnlocked = Boolean(workspace?.subscription?.plan?.allowClone);
    const [pickedDesign, setPickedDesign] = React.useState(campaign.pageDesign ?? null);
    const [pickedPattern, setPickedPattern] = React.useState(campaign.backgroundPattern ?? null);
    const [pickedRandom, setPickedRandom] = React.useState(Boolean(campaign.randomBackgroundPattern));
    const { mutate: patchPageDesign, isPending: isUpdatingPageDesign } = useMutation({
        mutationFn: patchCampaignPageDesign,
        onSuccess: (data) => toast.success(data.message),
        onError: errorHandler,
    });
    const { mutate: patchBgPattern, isPending: isUpdatingBgPattern } = usePatchBackgroundPattern();
    const { mutate: patchRandomBgPattern, isPending: isUpdatingRandomBgPattern } = usePatchRandomBackgroundPattern();
    const disabled = isUpdatingPageDesign || isUpdatingBgPattern || isUpdatingRandomBgPattern;
    return (<div className="flex h-[calc(100dvh-var(--header-h,4rem)-var(--tab-nav-h,3rem))] flex-col px-4 sm:px-6">
            <CampaignCustomizePanel mode={campaign.mode} selectedDesignId={pickedDesign} selectedPatternSlug={pickedPattern} randomEnabled={pickedRandom} proUnlocked={proUnlocked} workspaceId={workspace?.publicId} disabled={disabled} onPickDesign={(designId) => {
            setPickedDesign(designId);
            patchPageDesign({ publicId: campaign.publicId, pageDesign: designId });
        }} onPickPattern={(slug) => {
            setPickedPattern(slug);
            patchBgPattern({ publicId: campaign.publicId, backgroundPattern: slug });
        }} onToggleRandom={(enabled) => {
            setPickedRandom(enabled);
            patchRandomBgPattern({
                publicId: campaign.publicId,
                randomBackgroundPattern: enabled,
            });
        }}/>
        </div>);
}
